import { Checkbox, RadioGroup, RadioGroupItem, TextArea } from '@zep-us/zus';
import { cn } from '@/utils/tailwind.util';
import { useAiBuilderChatCopy } from '../hooks/useAiBuilderChatCopy';
import { type AiBuilderPendingQuestionSetQuestion } from '../store/types';
type AiBuilderQuestionAnswerControlsBaseProps = {
  question: AiBuilderPendingQuestionSetQuestion;
  locked?: boolean;
  unavailableOptionIds?: readonly string[];
};
type AiBuilderQuestionAnswerSingleProps = AiBuilderQuestionAnswerControlsBaseProps & {
  answerMode?: 'choice';
  selectedOptionId: string | null;
  onSelectedOptionChange: (optionId: string) => void;
  selectedOptionIds?: never;
  onSelectedOptionIdsChange?: never;
};
type AiBuilderQuestionAnswerMultipleProps = AiBuilderQuestionAnswerControlsBaseProps & {
  answerMode?: 'choice';
  selectedOptionId?: never;
  onSelectedOptionChange?: never;
  selectedOptionIds: readonly string[];
  onSelectedOptionIdsChange: (optionIds: readonly string[]) => void;
};
type AiBuilderQuestionAnswerDirectProps = AiBuilderQuestionAnswerControlsBaseProps & {
  answerMode: 'text';
  directText: string;
  onDirectTextChange: (text: string) => void;
  selectedOptionId?: never;
  onSelectedOptionChange?: never;
  selectedOptionIds?: never;
  onSelectedOptionIdsChange?: never;
};
export type AiBuilderQuestionAnswerControlsProps = AiBuilderQuestionAnswerSingleProps | AiBuilderQuestionAnswerMultipleProps | AiBuilderQuestionAnswerDirectProps;
const EMPTY_OPTION_IDS: readonly string[] = [];

/**
 * 질문 세트의 controlled 답변 control.
 *
 * C11A1/A2는 single/multiple choice branch를 소유한다. direct branch는 후속
 * 작업에서 이 표면에 추가한다.
 */
export const AiBuilderQuestionAnswerControls = (props: AiBuilderQuestionAnswerControlsProps) => {
  const copy = useAiBuilderChatCopy();
  const {
    question,
    locked = false,
    unavailableOptionIds = EMPTY_OPTION_IDS
  } = props;
  if (props.answerMode === 'text') {
    if (!question.allowDirectInput) return null;
    const inputId = `ai-builder-question-${question.questionId}-direct-input`;
    return <TextArea.Root value={props.directText} disabled={locked} required={false}>
        <label htmlFor={inputId} className={cn('text-body-sm-semibold text-neutral', locked && 'text-disabled')}>
          {copy.questionSet.directInput.label}
        </label>
        <TextArea.Input id={inputId} value={props.directText} disabled={locked} aria-label={copy.questionSet.directInput.ariaLabel} placeholder={copy.questionSet.directInput.placeholder} autoSize minRows={3} maxRows={6} wrapperClassName="mt-2xs bg-default" className="min-h-20 whitespace-pre-wrap break-words text-body-md-medium text-default placeholder:text-assistive" onChange={event => props.onDirectTextChange(event.target.value)} />
      </TextArea.Root>;
  }
  if (question.options.length === 0) {
    return null;
  }
  const unavailableOptionIdSet = new Set(unavailableOptionIds);
  if (question.selectionMode === 'multiple') {
    if (!('selectedOptionIds' in props) || !props.selectedOptionIds || !props.onSelectedOptionIdsChange) {
      return null;
    }
    const {
      onSelectedOptionIdsChange,
      selectedOptionIds
    } = props;
    const selectedOptionIdSet = new Set(selectedOptionIds);
    return <div role="group" aria-label={copy.choice.multipleSelectionAriaLabel} aria-disabled={locked} className="flex min-w-0 flex-col gap-2xs">
        {question.options.map(option => {
        const selected = selectedOptionIdSet.has(option.optionId);
        const unavailable = unavailableOptionIdSet.has(option.optionId);
        const updateSelection = (checked: boolean) => {
          const nextSelectedOptionIdSet = new Set(selectedOptionIds);
          if (checked) {
            nextSelectedOptionIdSet.add(option.optionId);
          } else {
            nextSelectedOptionIdSet.delete(option.optionId);
          }
          onSelectedOptionIdsChange(question.options.map(candidate => candidate.optionId).filter(optionId => nextSelectedOptionIdSet.has(optionId)));
        };
        return <label key={option.optionId} className={optionShellClassName({
          locked,
          selected,
          unavailable
        })}>
              <Checkbox name={`ai-builder-question-${question.questionId}`} checked={selected} disabled={locked || unavailable} aria-label={option.label} className="mt-3xs" onCheckedChange={checked => updateSelection(checked === true)} />
              <AnswerOptionContent label={option.label} description={option.description} selected={selected} disabled={locked || unavailable} />
            </label>;
      })}
      </div>;
  }
  if (!('selectedOptionId' in props)) return null;
  return <RadioGroup name={`ai-builder-question-${question.questionId}`} aria-label={copy.choice.singleSelectionAriaLabel} value={props.selectedOptionId ?? undefined} onValueChange={props.onSelectedOptionChange} disabled={locked} orientation="vertical" className="flex min-w-0 flex-col gap-2xs" data-sentry-element="RadioGroup" data-sentry-component="AiBuilderQuestionAnswerControls" data-sentry-source-file="AiBuilderQuestionAnswerControls.tsx">
      {question.options.map(option => {
      const selected = option.optionId === props.selectedOptionId;
      const unavailable = unavailableOptionIdSet.has(option.optionId);
      return <label key={option.optionId} className={optionShellClassName({
        locked,
        selected,
        unavailable
      })}>
            <RadioGroupItem value={option.optionId} disabled={unavailable} aria-label={option.label} className="mt-3xs" />
            <AnswerOptionContent label={option.label} description={option.description} selected={selected} disabled={locked || unavailable} />
          </label>;
    })}
    </RadioGroup>;
};
const optionShellClassName = ({
  locked,
  selected,
  unavailable
}: {
  locked: boolean;
  selected: boolean;
  unavailable: boolean;
}) => cn('flex min-w-0 items-start gap-sm rounded-md bg-default px-sm py-xs text-left transition-colors duration-150 ease-out', 'hover:bg-neutral focus-within:bg-neutral', selected && 'bg-strong-alternative text-strong', (locked || unavailable) && 'cursor-not-allowed bg-neutral text-disabled');
const AnswerOptionContent = ({
  label,
  description,
  selected,
  disabled
}: {
  label: string;
  description: string | null;
  selected: boolean;
  disabled: boolean;
}) => <span className="flex min-w-0 flex-1 flex-col gap-4xs" data-sentry-component="AnswerOptionContent" data-sentry-source-file="AiBuilderQuestionAnswerControls.tsx">
    <span className={cn('min-w-0 whitespace-normal break-words text-body-md-semibold text-default', selected && 'text-strong', disabled && 'text-disabled')}>
      {label}
    </span>
    {description && <span className={cn('min-w-0 whitespace-normal break-words text-body-sm-medium text-neutral', disabled && 'text-disabled')}>
        {description}
      </span>}
  </span>;